import { authGet, getAuthToken } from '../casl/authRequest';
import { getUserIdFromToken, getUserProfileHintFromToken } from '../casl/jwtUtils';
import {
  getResolvedRoleName,
  getRoleFromUser,
  getUserId,
  mergeWebUserWithRole,
  normalizeWebUserRecord,
  parseRoleFromApiData,
} from '../utils/apiUtils';

const hasProfileFields = (record) => {
  if (!record || typeof record !== 'object') {
    return false;
  }

  return Boolean(
    record.name || record.username || record.phone_number || getUserId(record) != null
  );
};

const loadWebUser = async (url, token) => {
  const response = await authGet(url, token);
  const record = normalizeWebUserRecord(response.data);
  return hasProfileFields(record) ? record : null;
};

const loadRole = async (userId, token, record) => {
  const embedded = getRoleFromUser(record);
  if (embedded?.name) {
    return embedded;
  }

  if (userId == null) {
    return embedded;
  }

  try {
    const response = await authGet(`/web_users/${userId}/role`, token);
    return parseRoleFromApiData(response.data) ?? embedded;
  } catch (error) {
    if (error?.response?.status === 404) {
      return embedded;
    }
    return embedded;
  }
};

export const fetchCurrentUserProfile = async (authToken = null) => {
  const token = getAuthToken(authToken);
  if (!token) {
    return null;
  }

  const hint = getUserProfileHintFromToken(token) ?? {};
  let record = null;

  try {
    record = await loadWebUser('/web_users/me', token);
  } catch {
    record = null;
  }

  if (!record) {
    const userId = getUserIdFromToken(token);
    if (userId) {
      try {
        record = await loadWebUser(`/web_users/${userId}`, token);
      } catch {
        record = null;
      }
    }
  }

  const mergedSource = { ...hint, ...(record ?? {}) };
  if (!hasProfileFields(mergedSource)) {
    return null;
  }

  const userId = getUserId(mergedSource) ?? getUserIdFromToken(token);
  const role = await loadRole(userId, token, mergedSource);
  const user = mergeWebUserWithRole(mergedSource, role);

  return {
    name: user.name ?? null,
    username: user.username ?? null,
    phone_number: user.phone_number ?? null,
    roleName: getResolvedRoleName(user, role),
  };
};
